import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Cart, User } from '../types';

export type CartPayload = {
  cart: Cart | null;
  user: User | null;
};

export type CartState = CartPayload & {
  /**
   * Whether we know yet.
   *
   * False on the first render, always — including in the browser. The server draws the
   * same markup for every visitor so that it can be cached, and hydration then requires
   * the browser's first render to match it exactly, so neither side may start with a
   * cart in hand.
   *
   * It stays false for longer when the page carried no cart and we had to ask the api.
   * Anything that would look wrong in that window should wait on this rather than treat
   * the empty state as an answer. Banner does, for its restricted banners.
   */
  loaded: boolean;
};

/** What the cart operations in this folder use to write back what the api returned. */
export type CartContextValue = CartState & {
  setCart: (cart: Cart | null) => void;
};

const EMPTY: CartState = { cart: null, user: null, loaded: false };

const CartContext = createContext<CartContextValue>({ ...EMPTY, setCart: () => {} });

const CART_URL = '/services_api/storefront/v1/cart.json';
const USER_URL = '/services_api/storefront/v1/user.json';

/**
 * The payload the page carries, or undefined when it carries none.
 *
 * The distinction matters more than it looks. An element is an answer, whatever it
 * holds, and asking the api anyway would be a wasted request on every page that already
 * knows. No element at all is not an answer: the page was rendered without booting
 * legacy, so nobody has looked yet.
 */
function readPayload(): CartPayload | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const el = document.getElementById('dm-v2-cart');

  if (!el) {
    return undefined;
  }

  if (!el.textContent) {
    return { cart: null, user: null };
  }

  try {
    return JSON.parse(el.textContent) as CartPayload;
  } catch (error) {
    console.error('Could not parse the V2 cart payload', error);

    return { cart: null, user: null };
  }
}

async function getJson<T>(url: string): Promise<T | null> {
  try {
    const response = await fetch(url, { credentials: 'same-origin' });

    // A 401 is an answer rather than a failure — it is what the user endpoint says
    // when nobody is signed in, which is most visitors.
    return response.ok ? ((await response.json()) as T) : null;
  } catch (error) {
    console.error(`Could not load ${url}`, error);

    return null;
  }
}

/**
 * The cart is asked for first, the user only if one came back.
 *
 * UserController extends CartAwareController and calls assertCart(), so the ordering is
 * forced: without a cart there is nothing for it to answer with. In practice the cart
 * always comes back — a cart here is the visitor's session rather than a basket of
 * products, and the endpoint mints one for anybody who asks — so the second request
 * effectively always follows.
 *
 * Which is the thing to remember about this path: reaching it *creates a session*. That
 * is fine for someone browsing, and it is exactly the cost a page besieged by bots
 * should not pay, so such a page should defer this rather than rely on it being cheap.
 */
async function loadFromApi(): Promise<CartPayload> {
  const cart = await getJson<Cart>(CART_URL);

  return { cart, user: cart ? await getJson<User>(USER_URL) : null };
}

// Shared, so two providers on one page cannot both ask.
let pending: Promise<CartPayload> | undefined;

function load(): Promise<CartPayload> {
  const payload = readPayload();

  if (payload) {
    return Promise.resolve(payload);
  }

  if (!pending) {
    pending = loadFromApi();
  }

  return pending;
}

/**
 * Holds the visitor's cart and signed-in user.
 *
 * Where they come from is this component's business and nobody else's. A page rendered
 * by the legacy stack carries them already, which is free; a page rendered without
 * booting legacy carries nothing, and then they are fetched. Consumers see the same
 * thing either way, a render later.
 *
 * The read happens in an effect rather than during render, which is what hydration
 * needs: effects run after the commit, so the browser's first render matches the
 * server's empty one instead of contradicting it.
 *
 * `value` is for the server renderer, which passes the empty state explicitly.
 */
export function CartProvider({
  value,
  loaded: initiallyLoaded = false,
  children,
}: {
  value?: CartPayload;
  loaded?: boolean;
  children: ReactNode;
}) {
  const [state, setState] = useState<CartState>({ ...(value ?? EMPTY), loaded: initiallyLoaded });

  useEffect(() => {
    let cancelled = false;

    load().then((payload) => {
      if (!cancelled) {
        setState({ ...payload, loaded: true });
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // A cart operation answers with the cart it produced, and writing it back here is
  // what keeps the badge in the header honest without a second request.
  const setCart = (cart: Cart | null) => setState((current) => ({ ...current, cart, loaded: true }));

  return <CartContext.Provider value={{ ...state, setCart }}>{children}</CartContext.Provider>;
}

/** Everything the provider holds. Prefer useCart or useUser. */
export function useCartState(): CartContextValue {
  return useContext(CartContext);
}
